/**
 * Presentation helpers for experiment views.
 *
 * Pure logic lives in experimentFormat.ts (unit tested); this file holds the
 * Cloudscape-rendering pieces and re-exports the formatters so callers have a
 * single import site.
 */
import StatusIndicator from '@cloudscape-design/components/status-indicator';
import { ExperimentResultRow, ExperimentStatus } from '../../types/experiment';
import { downloadCsv, csvDateStamp } from '../../utils/csvExport';

export {
  ALL_SEVERITY_OPTIONS,
  CAP_DEFAULT,
  CAP_MAX,
  POLL_INTERVAL_MS,
  STAGE_LABELS,
  absoluteTime,
  buildExperimentFilters,
  capError,
  countsText,
  dateRangeError,
  groupsText,
  isActive,
  progressText,
  scopeText,
  severitiesText,
  severityLabel,
  toGroupOptions,
  toSeverityOptions,
} from './experimentFormat';

export function ExperimentStatusBadge({ status }: { status: ExperimentStatus }) {
  if (status === 'SUCCEEDED') return <StatusIndicator type="success">Succeeded</StatusIndicator>;
  if (status === 'FAILED') return <StatusIndicator type="error">Failed</StatusIndicator>;
  if (status === 'CANCELLED') return <StatusIndicator type="stopped">Cancelled</StatusIndicator>;
  if (status === 'CANCELLING')
    return <StatusIndicator type="in-progress">Cancelling</StatusIndicator>;
  return (
    <StatusIndicator type="in-progress">
      {status === 'PENDING' ? 'Queued' : 'Running'}
    </StatusIndicator>
  );
}

/**
 * Export the comparison as CSV so win-rate can be tallied outside the app.
 * v1 has no in-app rating (deferred — P490393473), so this is how a reviewer
 * turns eyeballing into a number.
 */
export function resultsToCsv(runId: string, rows: ExperimentResultRow[]) {
  downloadCsv(
    `ai-experiment-${runId.slice(0, 8)}-${csvDateStamp()}.csv`,
    ['ticketId', 'title', 'productionSummary', 'experimentSummary', 'latencyMs', 'error'],
    rows.map((r) => [
      r.ticketId,
      r.title ?? '',
      r.productionSummary ?? '',
      r.experimentSummary ?? '',
      r.latencyMs ?? '',
      r.error ?? '',
    ]),
  );
}
